import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/hooks/use-language';
import { useToast } from '@/hooks/use-toast';
import { useGroups } from '@/hooks/use-groups';
import { getGroupColorClasses, getLocalizedGroupName } from '@/utils/groupColors';
import { devLog } from '@/utils/console-utils';
import { cn } from '@/lib/utils';

interface Member {
  id: number;
  name: string;
  phone: string;
  email: string;
  groups: string[];
  subgroups: string[];
  isAdmin: boolean;
}

interface EditMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: Member | null;
  onUpdateMember: (updatedMember: Member) => void;
}


export function EditMemberDialog({ open, onOpenChange, member, onUpdateMember }: EditMemberDialogProps) {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const { allGroups, isLoading: groupsLoading } = useGroups();

  const [formData, setFormData] = React.useState<Member | null>(null);
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  // Separate parent groups (main groups) from subgroups using allGroups
  const parentGroups = React.useMemo(() => 
    allGroups.filter(g => !g.parent_id), [allGroups]
  );
  const subgroups = React.useMemo(() => 
    allGroups.filter(g => g.parent_id), [allGroups]
  );

  // Initialize form data when member changes
  React.useEffect(() => {
  if (member && allGroups.length > 0) {
      // Get flat lists of group names
      const flatGroups = (member.groups || []);
      
      // Separate into parent groups and subgroups based on the database structure
      const memberParentGroups: string[] = [];
      const memberSubgroups: string[] = [];
      
      flatGroups.forEach(groupName => {
        const isParentGroup = parentGroups.some(g => g.name === groupName);
        const isSubgroup = subgroups.some(g => g.name === groupName);
        
        if (isParentGroup) {
          memberParentGroups.push(groupName);
        } else if (isSubgroup) {
          memberSubgroups.push(groupName);
        }
      });
      
      setFormData({ 
        ...member,
        groups: memberParentGroups,
        subgroups: memberSubgroups
      });
      devLog.log('Initialized form data - all groups:', flatGroups, 'parent groups:', memberParentGroups, 'subgroups:', memberSubgroups);
    }
  }, [member, allGroups, parentGroups, subgroups]);

  const validateForm = () => {
    if (!formData) return false;
    
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t("validation.required");
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t("validation.required");
    } else if (!/^\+?[\d\s-]+$/.test(formData.phone)) {
      newErrors.phone = t("validation.phoneFormat");
    }

    if (!formData.email.trim()) {
      newErrors.email = t("validation.required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t("validation.emailFormat");
    }

    if (formData.groups.length === 0) {
      newErrors.groups = t("validation.selectGroup");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (formData && validateForm()) {
      // Combine both groups and subgroups into the groups array for the API
      const allSelectedGroups = [...formData.groups, ...formData.subgroups];
      const updatedMember = {
        ...formData,
        groups: allSelectedGroups
      };
      devLog.log('Submitting member update with groups:', allSelectedGroups);
      onUpdateMember(updatedMember);
      onOpenChange(false);
      toast({
        title: t("members.memberUpdated"),
        description: t("members.memberUpdatedDesc", { name: formData.name })
      });
    }
  };

  const handleCancel = () => {
    if (member) {
      setFormData({ ...member });
    }
    setErrors({});
    onOpenChange(false);
  };

  const handleGroupToggle = (group: string) => {
    if (!formData) return;
    
    setFormData(prev => prev ? {
      ...prev,
      groups: prev.groups.includes(group)
        ? prev.groups.filter(g => g !== group)
        : [...prev.groups, group]
    } : null);
  };

  const handleSubgroupToggle = (subgroup: string) => {
    if (!formData) return;
    
    setFormData(prev => prev ? {
      ...prev,
      subgroups: prev.subgroups.includes(subgroup)
        ? prev.subgroups.filter(s => s !== subgroup)
        : [...prev.subgroups, subgroup]
    } : null);
  };

  if (!formData) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("members.editMember")} - {member?.name}</DialogTitle>
          <DialogDescription>
            {t("members.editMemberDescription")}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground">{t("members.basicInfo")}</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t("members.name")} *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => prev ? { ...prev, name: e.target.value } : null)}
                  placeholder={t("members.enterName")}
                />
                {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">{t("members.phone")} *</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => prev ? { ...prev, phone: e.target.value } : null)}
                  placeholder={t("placeholder.phoneNumber")}
                />
                {errors.phone && <p className="text-sm text-destructive">{errors.phone}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t("members.email")} *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => prev ? { ...prev, email: e.target.value } : null)}
                placeholder={t("placeholder.email")}
              />
              {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
            </div>
          </div>

          {/* Admin Privileges */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground">{t("members.roleAndPermissions")}</h3>
            
            <div
              className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-muted/50 -mx-2"
              onClick={() => setFormData(prev => prev ? { ...prev, isAdmin: !prev.isAdmin } : null)}
            >
              <Checkbox
                id="isAdmin"
                checked={formData.isAdmin}
                onChange={() => {}} // Controlled by parent div
              />
              <Label htmlFor="isAdmin" className="cursor-pointer flex-1">{t("members.adminPrivileges")}</Label>
            </div>
          </div>

          {/* Group Selection */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground">{t("members.groupMembership")}</h3>
            
            <div className="space-y-2">
              <Label>{t("members.groups")} *</Label>
              {groupsLoading ? (
                <div className="text-sm text-muted-foreground">Loading groups...</div>
              ) : (
                <div className="space-y-2">
                  {parentGroups.map(group => {
                    const colors = getGroupColorClasses(group.name);
                    return (
                      <div 
                        key={group.id} 
                        className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-muted/50 -mx-2"
                        onClick={() => handleGroupToggle(group.name)}
                      >
                        <Checkbox
                          checked={formData.groups.includes(group.name)}
                          onChange={() => {}} // Controlled by parent div
                        />
                        <Badge 
                          variant="outline"
                          className={cn("text-xs border-transparent", colors.bg, colors.text)}
                        >
                          {getLocalizedGroupName(group.name, language)}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              )}
              {errors.groups && <p className="text-sm text-destructive">{errors.groups}</p>}
            </div>

            <div className="space-y-2">
              <Label>{t("members.subgroups")}</Label>
              {groupsLoading ? (
                <div className="text-sm text-muted-foreground">Loading subgroups...</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {subgroups.map(subgroup => {
                    const colors = getGroupColorClasses(subgroup.name);
                    return (
                      <div 
                        key={subgroup.id} 
                        className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-muted/50 -mx-2"
                        onClick={() => handleSubgroupToggle(subgroup.name)}
                      >
                        <Checkbox
                          checked={formData.subgroups.includes(subgroup.name)}
                          onChange={() => {}} // Controlled by parent div
                        />
                        <Badge 
                          variant="outline" 
                          className="text-xs"
                          style={{
                            borderColor: `hsl(var(--group-${colors.variant}))`,
                            color: `hsl(var(--group-${colors.variant}))`
                          }}
                        >
                          {getLocalizedGroupName(subgroup.name, language)}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-2 pt-4 border-t">
          <Button variant="outline" onClick={handleCancel}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSubmit}>
            {t("members.updateMember")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
